import {useState, useCallback, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import Link from 'next/link'
import {classnames} from '@@client/lib/classes'
import {useTokenCache} from '@@client/lib/hooks'
import {login as loginAction, loginClear} from '@@client/account/login/actions'
import {recoverPassword, recoverPasswordClear} from '@@client/account/recover/actions'
import {bigBlueStyle, actionLinkStyle} from './checkout-styles'
import LoginForm from '@@client/account/login/LoginForm'
import RecoverPasswordForm from '@@client/account/recover/RecoverPasswordForm'
import PrivacyTermsBrief from '@@client/components/PrivacyTermsBrief'
import Messages from '@@client/components/Messages'
import Col100 from '@@client/components/Col100'
import SectionHeader from './SectionHeader'

const SubStepLoginForm = ({onForgotClick, onLogin}) => {
    const tokenCache = useTokenCache()
    const dispatch = useDispatch()
    const login = useSelector(state => state.account.login)

    useEffect(() => { dispatch(loginClear()) }, [])

    const handleSubmit = useCallback((params) => {
        // already logging in
        if(login.doing) return

        dispatch(loginAction(tokenCache, params))
            .then(success => {
                if(success) onLogin()
            })
    }, [login])

    return (
        <>
            <p className={bigBlueStyle.className}>Sign in for a faster checkout:</p>

            <Messages error={login.errors} />

            <LoginForm
                idPrefix='checkout-login'
                onSubmit={handleSubmit}
                paramErrors={login.paramErrors}
                disabled={login.doing}
            />

            <p className='forgot'><button className='m--as-link' onClick={onForgotClick}>Forgot your password?</button></p>

            <style jsx>
                {`
                    .forgot {
                        margin-top: 25px;
                        text-align: center;
                    }
                `}
            </style>
        </>
    )
}

const SubStepForgotForm = ({onSubmit, onLoginClick}) => {
    const dispatch = useDispatch()
    const recover = useSelector(state => state.account.recover)

    useEffect(() => { dispatch(recoverPasswordClear()) }, [])

    const handleSubmit = useCallback((params) => {
        // already logging in
        if(recover.doing) return

        dispatch(recoverPassword(tokenCache, params))
            .then(success => {
                if(success) onSubmit()
            })
    }, [recover])

    const tokenCache = useTokenCache()

    return (
        <>
            <p className={bigBlueStyle.className}>Recover your password:</p>

            <Messages error={recover.errors} />

            <div className='the-form'>
                <RecoverPasswordForm
                    idPrefix='checkout-recover'
                    onSubmit={handleSubmit}
                    paramErrors={recover.paramErrors}
                    disabled={recover.doing}
                />
            </div>

            <p className='login'>Remembered your password? <button className='m--as-link' onClick={onLoginClick}>Login</button></p>

            <style jsx>
                {`
                    .login {
                        margin-top: 25px;
                        text-align: center;
                    }

                    .the-form :global(.actions) {
                        margin-top: 30px;
                    }
                `}
            </style>
        </>
    )
}

export default function StepLogin({onLogin = () => {}, onGuest}) {
    const [subStep, setSubStep] = useState('login')

    return (
        <>
            <div className='outer'>
                <Col100>
                    <div className='heading'>
                        <h1>Checkout</h1>
                        <div><Link href='/cart'><a className={classnames('c--back', actionLinkStyle.className)}>Back to cart</a></Link></div>
                    </div>

                    <div className='items-container'>
                        <div className='login-register'>
                            <div className='roundy-box'>
                                <SectionHeader>Returning Customers</SectionHeader>

                                {
                                    {
                                        'login': () => (
                                            <SubStepLoginForm
                                                onLogin={onLogin}
                                                onForgotClick={() => setSubStep('forgot')}
                                            />
                                        ),
                                        'forgot': () => (
                                            <SubStepForgotForm
                                                onSubmit={() => setSubStep('login')}
                                                onLoginClick={() => setSubStep('login')}
                                            />
                                        ),
                                    }[subStep]()
                                }

                                <div className='privacy'>
                                    <PrivacyTermsBrief />
                                </div>
                            </div>
                        </div>
                        <div className='guest'>
                            <div className='roundy-box'>
                                <SectionHeader>New Customers</SectionHeader>

                                <p className={bigBlueStyle.className}>Don't have an account? No problem!</p>

                                <p className={bigBlueStyle.className}>You can check out as a guest. You'll have the option to create an account during the checkout process.</p>

                                <p className='as-guest-p'><button className='btn btn--yellow continue-as-guest' onClick={onGuest}>Continue as a Guest</button></p>
                            </div>
                        </div>
                    </div>
                </Col100>
            </div>
            {bigBlueStyle.styles}
            {actionLinkStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .outer {
                        background: $c--gray--bg;
                        padding: 30px 0 100px;
                    }

                    .heading {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }

                    .items-container {
                        display: flex;
                        flex-direction: column-reverse;

                        @media(--md) {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                        }
                    }

                    .as-guest-p,
                    .outer :global(.actions) {
                        text-align: center;
                    }

                    .as-guest-p {
                        margin-top: 30px;
                    }

                    .login-register {
                        @media(--md) {
                            padding-right: 30px;
                        }
                    }

                    .guest {
                        margin-bottom: 20px;
                        padding-bottom: 20px;
                        border-bottom: 1px solid $c--blue--light-gray;

                        @media(--md) {
                            padding-left: 30px;
                            padding-bottom: 0;
                            margin-bottom: 0;
                            border-left: 1px solid $c--blue--light-gray;
                            border-bottom: 0;
                        }
                    }

                    .roundy-box {
                        border: 1px solid $c--gray--medium;
                        border-radius: 6px;
                        background: white;
                        padding: 20px;
                        height: 100%;
                    }

                    .privacy {
                        margin: 25px auto 0;
                        text-align: center;
                        max-width: 275px;
                    }
                `}
            </style>
        </>
    )
}
